import { StandardEvents } from '@shopify/events';

/**
 * Formats an amount (in cents) using a Shopify money_format string.
 * Supports the standard {{amount}} placeholder variants.
 */
function formatMoney(cents, format) {
  cents = Number(cents) || 0;
  const value = cents / 100;

  const withCommas = (num, decimals, thousands, decimalSep) => {
    const fixed = (Math.abs(num)).toFixed(decimals);
    let [whole, frac] = fixed.split('.');
    whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? whole + decimalSep + frac : whole;
  };

  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
    switch (token) {
      case 'amount':
        return withCommas(value, 2, ',', '.');
      case 'amount_no_decimals':
        return withCommas(value, 0, ',', '.');
      case 'amount_with_comma_separator':
        return withCommas(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return withCommas(value, 0, '.', ',');
      case 'amount_with_space_separator':
        return withCommas(value, 2, ' ', ',');
      case 'amount_no_decimals_with_space_separator':
        return withCommas(value, 0, ' ', ',');
      default:
        return withCommas(value, 2, ',', '.');
    }
  });
}

/*
 * Free shipping depends on the province (window.gdjFsZone, planted by the
 * announcement block): the main threshold for zone.mainProvinces, the other one
 * for every other Canadian province. Province = the signed-in customer's address
 * (zone.province, set in Liquid), else the visitor's IP via the engine
 * (GET /api/v1/geo -> region, cached 7 days). Unknown / outside Canada = main.
 */
const REGION_KEY = 'gdj_geo_region';
const REGION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function cachedRegion() {
  try {
    const v = JSON.parse(localStorage.getItem(REGION_KEY) || 'null');
    if (v && Date.now() - v.t < REGION_TTL_MS) return { region: v.r || null };
  } catch (_) {}
  return null;
}

function thresholdFor(zone, province) {
  if (!province) return { cents: zone.mainCents, display: zone.mainDisplay };
  return (zone.mainProvinces || []).includes(province)
    ? { cents: zone.mainCents, display: zone.mainDisplay }
    : { cents: zone.otherCents, display: zone.otherDisplay };
}

let zonePromise = null;
/** Resolves to { cents, display } or null when there is no zone config. */
function resolveZone() {
  if (zonePromise) return zonePromise;
  const zone = window.gdjFsZone;
  if (!zone) return (zonePromise = Promise.resolve(null));
  if (zone.province) return (zonePromise = Promise.resolve(thresholdFor(zone, zone.province)));
  const cached = cachedRegion();
  if (cached) return (zonePromise = Promise.resolve(thresholdFor(zone, cached.region)));
  zonePromise = (async () => {
    let region = null;
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 2500);
      const res = await fetch(zone.geoUrl, { credentials: 'omit', signal: ctrl.signal });
      clearTimeout(timer);
      if (res.ok) {
        const j = await res.json();
        region = j && j.country === 'CA' && j.region ? String(j.region).toUpperCase() : null;
        try {
          localStorage.setItem(REGION_KEY, JSON.stringify({ r: region, t: Date.now() }));
        } catch (_) {}
      }
    } catch (_) {
      /* unknown → main threshold */
    }
    return thresholdFor(zone, region);
  })();
  return zonePromise;
}

class GdjFreeShipping extends HTMLElement {
  connectedCallback() {
    // Instances without their own threshold (the cart-drawer bar) inherit it
    // from the announcement-bar block, which plants window.gdjFsThresholdCents.
    this.thresholdCents = Number(this.dataset.thresholdCents) || Number(window.gdjFsThresholdCents) || 0;
    if (!this.thresholdCents) {
      this.hidden = true;
      return;
    }
    // Province-based threshold: swap it in (and re-render) once known.
    resolveZone().then((t) => {
      if (!t || !Number(t.cents) || Number(t.cents) === this.thresholdCents) return;
      this.thresholdCents = Number(t.cents);
      this.thresholdDisplay = t.display;
      window.gdjFsThresholdCents = this.thresholdCents;
      this.refresh();
    });
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.textEl = this.querySelector('[data-gdj-free-shipping-text]');

    // Build the progress bar + "free shipping" goal pill once
    // (styles live in gdj-ship-progress.liquid).
    if (!this.querySelector('.gdj-fsbar')) {
      const track = document.createElement('span');
      track.className = 'gdj-fsbar';
      track.innerHTML = '<span class="gdj-fsbar__fill"></span>';
      this.appendChild(track);

      const goal = document.createElement('span');
      goal.className = 'gdj-fsbar__goal';
      goal.innerHTML =
        '<svg class="gdj-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>' +
        '<span>' + (window.gdjFsGoal || 'Free shipping') + '</span>';
      this.appendChild(goal);
    }
    this.fillEl = this.querySelector('.gdj-fsbar__fill');

    this.onCartUpdate = this.onCartUpdate.bind(this);
    this._ac = new AbortController();
    const { signal } = this._ac;
    // The standard event is the primary signal, but the announcement-bar
    // instance (inside the slideshow) proved unreliable at receiving it —
    // so listen broadly and mirror the header cart badge, which Horizon
    // re-renders on every cart change.
    [
      StandardEvents.cartLinesUpdate,
      'cart:update',
      'cart:updated',
      'cart:refresh',
      'shopify:cart:lines-update',
      'variant:add',
    ].forEach((ev) => document.addEventListener(ev, this.onCartUpdate, { signal }));
    window.addEventListener('pageshow', () => this.refresh(), { signal });

    // Observe the header SECTION wrapper, not the badge itself — morph
    // re-renders replace the badge node, which would orphan the observer.
    const badge = document.querySelector('.cart-bubble');
    const watchRoot = (badge && badge.closest('.shopify-section')) || badge;
    if (watchRoot && 'MutationObserver' in window) {
      this._mo = new MutationObserver(() => {
        clearTimeout(this._moTimer);
        this._moTimer = setTimeout(() => this.refresh(), 120);
      });
      this._mo.observe(watchRoot, { childList: true, subtree: true, characterData: true });
    }

    // Refresh once on load in case the cart changed on another page (bfcache).
    this.refresh();
  }

  disconnectedCallback() {
    if (this._ac) this._ac.abort();
    if (this._mo) this._mo.disconnect();
    clearTimeout(this._moTimer);
  }

  onCartUpdate() {
    // Always re-fetch: event payload shapes vary, and we need the
    // pre-coupon subtotal which not every payload carries.
    this.refresh();
  }

  async refresh() {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const cart = await res.json();
      // Basis is a theme-editor toggle on the announcement block
      // (window.gdjFsPreCoupon): true = product subtotal before discount
      // codes, false = discounted total.
      const preCoupon = window.gdjFsPreCoupon !== false;
      const basis = preCoupon && typeof cart.items_subtotal_price === 'number'
        ? cart.items_subtotal_price
        : cart.total_price;
      this.render(basis, cart.item_count);
    } catch (_) {
      /* leave the server-rendered message in place on failure */
    }
  }

  render(totalCents, itemCount) {
    if (!this.textEl) return;
    const remaining = Math.max(this.thresholdCents - (Number(totalCents) || 0), 0);
    let message;

    let pct;
    if (!itemCount) {
      const display = this.thresholdDisplay || this.dataset.thresholdDisplay || formatMoney(this.thresholdCents, this.moneyFormat);
      message = this.dataset.tmplBefore.replace('[[amount]]', display);
      pct = 0;
    } else if (remaining > 0) {
      message = this.dataset.tmplRemaining.replace('[[amount]]', formatMoney(remaining, this.moneyFormat));
      pct = this.thresholdCents ? ((Number(totalCents) || 0) / this.thresholdCents) * 100 : 0;
    } else {
      message = this.dataset.tmplUnlocked;
      pct = 100;
    }

    this.textEl.textContent = message;
    if (this.fillEl) this.fillEl.style.width = Math.max(0, Math.min(100, pct)) + '%';
    this.classList.toggle('gdj-fs--unlocked', itemCount > 0 && remaining <= 0);
    // Empty cart: hide the progress bar + goal pill, keep the message only.
    this.classList.toggle('gdj-fs--empty', !itemCount);
  }
}

if (!customElements.get('gdj-free-shipping')) {
  customElements.define('gdj-free-shipping', GdjFreeShipping);
}
